// @flow

import React from 'react'
import ReactDOM from 'react-dom'
import PT from 'prop-types'
import { createBrowserHistory } from 'history'
import { Redirect, Route, Router, Switch } from 'react-router-dom'
import * as o from 'rxjs/operators'
import AuthContainer from '@vimcar/shared-components/components/AuthContainer'
import LocalizationProvider from '@vimcar/shared-components/components/LocalizationProvider'
import { type DomainSwitch } from '@vimcar/shared-stores/mobx/DomainSwitch'
import { type Users } from '@vimcar/shared-stores/mobx/users'
import { type Userinfo } from '@vimcar/shared-stores/mobx/Userinfo'

import { mkAndroidMsgPoster, mkWebkitMsgPoster } from '#utils/dom'
import LocaleContainer from '#components/LocaleContainer'
import Logout from '#components/Logout'
import Dashboard from '#components/Dashboard'
import CreateBookingContainer from '#components/CreateBookingContainer'
import BookingsOverview from '#components/BookingsOverview'
import KioskProvider from '#components/KioskProvider'
import Profile from '#components/Profile'
import ShiftmoveIntegrationCheck from '#components/ShiftmoveIntegrationCheck'
import { VimcarBookingLogomarkNeutralInverse } from "#components/VimcarBookingLogomarkNeutralInverse";

type Models = {|
  domain: DomainSwitch<any>,
  userinfo: Userinfo<any>,
  users: Users,
|}

const history = createBrowserHistory()
const postAndroidMsg = mkAndroidMsgPoster('historyHandler')
const postWebkitMsg = mkWebkitMsgPoster('historyHandler')

history.listen((location) => {
  // Notify Android WebView clients
  postAndroidMsg(location.pathname)
  // Notify iOS WebView clients
  postWebkitMsg(location.pathname)
})

const paths = {
  entry: '/',
  logout: '/logout',
  dashboard: '/dashboard',
  createBooking: '/dashboard/new-booking',
  bookings: '/dashboard/bookings',
  profile: '/dashboard/profile',
}

const Routes = (props: any, ctx: { models: Models }) => {
  const loginUser = () => {
    return ctx.models.userinfo.fetch().pipe(
      o.tap((userinfoBody) => {
        ctx.models.users.addUser(userinfoBody.id, userinfoBody)
      }),
    )
  }

  const authContainerRef = React.useRef(null)

  React.useEffect(()=>{
    if(!authContainerRef.current) return
     const svgElement = authContainerRef.current.querySelector('div > div > div > svg');

    if (svgElement) {
      const parentElement = svgElement.parentNode;
      parentElement.removeChild(svgElement);

      ReactDOM.render(<VimcarBookingLogomarkNeutralInverse height={48} width={48} />, parentElement);
    }
  },[])

  return (
    <LocalizationProvider
      domain={ctx.models.domain}
      selfUser={ctx.models.users.self}
    >
      <KioskProvider location={history.location}>
        {/** $FlowFixMe failing to find ref */}
        <div ref={authContainerRef}>
        <AuthContainer fetchUserinfo={loginUser} history={history} theme='dark'>
          <LocaleContainer location={history.location}>
            <Router history={history}>
              <Switch>
                <Route component={Logout} path={paths.logout} />

                <Route
                  exact
                  path={paths.entry}
                  render={() => <Redirect to={paths.dashboard} />}
                />

                <Route
                  path={paths.dashboard}
                  render={() => (
                    <Dashboard>
                      <Route
                        exact
                        path={paths.dashboard}
                        render={() => <Redirect to={paths.createBooking} />}
                      />

                      <Route
                        path={paths.createBooking}
                        render={(routeProps) => (
                          <ShiftmoveIntegrationCheck routeContext="createBooking">
                            <CreateBookingContainer {...routeProps} />
                          </ShiftmoveIntegrationCheck>
                        )}
                      />

                      <Route
                        path={paths.bookings}
                        render={(routeProps) => (
                          <ShiftmoveIntegrationCheck routeContext="bookings">
                            <BookingsOverview {...routeProps} />
                          </ShiftmoveIntegrationCheck>
                        )}
                      />

                      <Route component={Profile} path={paths.profile} />
                    </Dashboard>
                  )}
                />
              </Switch>
            </Router>
          </LocaleContainer>
        </AuthContainer>
        </div>
      </KioskProvider>
    </LocalizationProvider>
  )
}

Routes.contextTypes = {
  models: PT.object,
}

export default Routes
